import { useEffect, useRef, useState } from 'react'

const usePress = (pressDelay = 300, closeDelay = 1500) => {
  const [isPressed, setIsPressed] = useState(false)

  const pressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null)
  const elementRef = useRef<Element | null>(null)

  const clearPressTimer = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const closeWithDelay = () => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      setIsPressed(false)
    }, closeDelay)
  }

  useEffect(() => {
    return () => {
      clearPressTimer()
      clearCloseTimer()
    }
  }, [])

  const onTouchStart = (e: React.TouchEvent<Element>) => {
    if (e.touches.length !== 1) return

    elementRef.current = e.currentTarget
    clearPressTimer()
    clearCloseTimer()

    pressTimerRef.current = setTimeout(() => {
      setIsPressed(true)
      closeWithDelay()
    }, pressDelay)
  }

  const onTouchMove = (e: React.TouchEvent<Element>) => {
    if (!elementRef.current || e.touches.length !== 1) return

    const touch = e.touches[0]
    const rect = elementRef.current.getBoundingClientRect()

    const isOutside =
      touch.clientX < rect.left ||
      touch.clientX > rect.right ||
      touch.clientY < rect.top ||
      touch.clientY > rect.bottom

    if (isOutside) {
      clearPressTimer()
      closeWithDelay()
    }
  }

  const onTouchEnd = () => {
    clearPressTimer()
    closeWithDelay()
  }

  const onTouchCancel = () => {
    clearPressTimer()
    closeWithDelay()
  }

  return [
    isPressed,
    { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
  ] as const
}

export default usePress
